
"use client"

import { useEffect, useState, useMemo } from "react"
import { useRouter } from "next/navigation"
import { useGenerationStore } from "@/store/generation-store"
import { useGeneration } from "@/hooks/use-generation"
import { toast } from "sonner"
import { showInsufficientCreditsToast } from "@/lib/insufficient-credits"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Sparkles, Upload, Camera, Image as ImageIcon, X, Loader2, RefreshCw, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import Image from "next/image"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { motion } from "framer-motion"
import Link from "next/link"
import { useAuth } from "@/components/auth-provider"
import { useAuthModal } from "@/store/auth-modal-store"
import { useUserStore } from "@/store/user-store"

import { type StudioPreset } from "./presets"
import { usePresetStore } from "@/store/preset-store"
import { MediaSelector } from "@/components/media-selector"
import { usePricingQuote } from "@/hooks/use-pricing-quote"
import modelsConfig from "@ai-studio/shared/catalog/models.image.json"
import { GenerationTimer } from "@/components/generation/generation-timer"
import { showGenerationStartedToast } from "@/lib/generation-started-toast"

// Find the model variant for pricing
const modelFamily = modelsConfig.models.find(m => m.id === "nano-banana-pro")
const modelVariant = modelFamily?.variants.find(v => v.id === "nano-banana-pro-image-to-image")

const CATEGORIES = [
  { id: 'sensualne', label: 'Чувственный' },
  { id: 'biznesowe', label: 'Бизнес' },
  { id: 'fashion', label: 'Мода' },
  { id: 'lifestyle', label: 'Лайфстайл' },
  { id: 'beauty', label: 'Бьюти' },
  { id: 'artistic_bw', label: 'Ч/Б Арт' },
  { id: 'photo_session', label: 'Фотосессия' },
]

interface AiPhotoStudioViewProps {
  isModal?: boolean;
  onClose?: () => void;
}

/** Map DB presets to StudioPreset UI format. DB is the only source of truth. */
function useStudioPresets(): StudioPreset[] {
    const { fetchByApp } = usePresetStore()
    const [presets, setPresets] = useState<StudioPreset[]>([])

    useEffect(() => {
        // Shared with the miniapp PhotoshootModePanel — one app, one source.
        fetchByApp('photoshoot').then((dbPresets) => {
            const mapped: StudioPreset[] = dbPresets.map((p) => ({
                id: p.id,
                namePl: p.title,
                nameEn: p.title,
                category: p.category ?? 'sensualne',
                thumbnail: p.media_url ?? '',
                prompt: p.prompt ?? '',
                aspectRatio: String(p.defaults?.aspectRatio ?? '3:4'),
                resolution: String(p.defaults?.resolution ?? '2K'),
            }))
            setPresets(mapped)
        })
    }, [fetchByApp])

    return presets
}

export function AiPhotoStudioView({ isModal = false, onClose }: AiPhotoStudioViewProps) {
    const router = useRouter()
    const {
        setPrompt,
        selectedModelId, setSelectedModelId,
        modelParams, updateModelParam,
        setAspectRatio,
        isGenerating,
    } = useGenerationStore()

    const { generate, resultUrl } = useGeneration()
    const studioPresets = useStudioPresets()

    // Local State
    const [selectedPresetId, setSelectedPresetId] = useState<string>('')
    const [inputImage, setInputImage] = useState<string | null>(null)
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false)
    const [activeCategory, setActiveCategory] = useState<string>('sensualne')
    const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null)

    // Reset timer when global generation flag flips off (success/failure handled inside use-generation hook)
    useEffect(() => {
        if (!isGenerating) setGenerationStartedAt(null)
    }, [isGenerating])

    // Select default model on mount
    useEffect(() => {
        setSelectedModelId('nano-banana-pro-image-to-image')
    }, [setSelectedModelId])

    // Select first preset when presets load
    useEffect(() => {
        if (studioPresets.length > 0 && !selectedPresetId) {
            setSelectedPresetId(studioPresets[0].id)
        }
    }, [studioPresets, selectedPresetId])

    // Update aspect ratio and quality when preset changes
    useEffect(() => {
        const preset = studioPresets.find(p => p.id === selectedPresetId)
        if (preset) {
            if (preset.aspectRatio) {
                setAspectRatio(preset.aspectRatio)
            }
            if (preset.resolution) {
                updateModelParam('quality', preset.resolution)
            }
        }
    }, [selectedPresetId, setAspectRatio, updateModelParam])

    const { user } = useAuth()
    const openAuthModal = useAuthModal((s) => s.open)
    const { credits } = useUserStore()

    const handleGenerate = async () => {
        if (!user) {
            openAuthModal({ message: "Войдите, чтобы создать AI-фото" })
            return
        }
        if (!inputImage) {
            toast.error("Загрузите фото")
            return
        }
        if (price != null && credits < price) {
            showInsufficientCreditsToast()
            return
        }

        const preset = studioPresets.find(p => p.id === selectedPresetId)
        if (!preset) return

        // Construct prompt
        let finalPrompt = preset.prompt
        
        // Clean up placeholders
        finalPrompt = finalPrompt
            .replace(/,?\s*weight:\s*\{\{weight\}\}/g, '')
            .replace(/,?\s*height:\s*\{\{height\}\}/g, '')
            .replace(/\{\{weight\}\}/g, '')
            .replace(/\{\{height\}\}/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            
        setPrompt(finalPrompt)

        // Set input image param
        updateModelParam('image_inputs', [inputImage])

        setGenerationStartedAt(Date.now())
        try {
            await generate()
            showGenerationStartedToast()
        } catch (error) {
            console.error(error)
            const message = error instanceof Error ? error.message : "Ошибка генерации"
            toast.error(message)
            setGenerationStartedAt(null)
        }
    }

    const selectedPreset = studioPresets.find(p => p.id === selectedPresetId)

    const handleClose = () => {
        onClose?.();
    };

    const filteredPresets = useMemo(() => {
        return studioPresets.filter(p => p.category === activeCategory)
    }, [activeCategory, studioPresets])

    // Calculate Price
    const pricingPayload = useMemo(() => {
        if (!modelVariant) return null
        return { kind: 'image' as const, model_id: modelVariant.id, params: {
            aspect_ratio: (modelParams.aspect_ratio as string) || selectedPreset?.aspectRatio || '3:4',
            quality: (modelParams.quality as string) || selectedPreset?.resolution || '1K'
        } }
    }, [modelVariant, selectedPreset, modelParams.aspect_ratio, modelParams.quality])
    const { quote: priceQuote } = usePricingQuote(pricingPayload)
    const price = priceQuote?.user_price_credits ?? null

    return (
        <div className={cn("flex flex-col bg-background text-foreground font-sans selection:bg-cyan-500/30 overflow-hidden relative", isModal ? "h-full" : "h-dvh")}>
            
            {/* Close Button (Modal only) - mimicking ProfessionalHeadshotView style */}
            {isModal && onClose && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClose}
                    className="absolute top-3 right-3 z-50 rounded-full bg-background/60 hover:bg-background/90 border border-border/30 h-9 w-9 text-muted-foreground hover:text-foreground transition-colors"
                >
                    <X className="w-4 h-4" />
                </Button>
            )}

            {/* Main Split Layout */}
            <main className="flex-1 flex flex-col lg:flex-row relative overflow-y-auto lg:overflow-hidden min-h-0">
                
                {/* Panel 01 - Photo Upload */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="w-full shrink-0 lg:shrink lg:min-h-0 h-auto lg:h-full lg:w-[45%] xl:w-[45%] bg-muted/5 border-b lg:border-b-0 lg:border-r border-border relative flex flex-col p-4 lg:p-6 overflow-y-auto scrollbar-hide"
                >
                    {/* Left Header */}
                    <div className={cn("flex items-center justify-between mb-3 lg:mb-6 shrink-0 h-10", isModal && "pr-10 lg:pr-0")}>
                        <div className="flex items-center gap-2.5">
                            {/* DashboardHeader provides mobile back-arrow. */}
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">1</div>
                            <h2 className="text-sm font-semibold text-foreground tracking-tight lg:font-bold lg:uppercase lg:tracking-wider">Ваше фото</h2>
                        </div>
                        {inputImage && !resultUrl && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setInputImage(null)} 
                                className="text-xs text-muted-foreground hover:text-destructive h-auto p-0 hover:bg-transparent"
                                disabled={isGenerating}
                            >
                                Сбросить
                            </Button>
                        )}
                    </div>

                    {/* Main Visual Area */}
                    <div className="flex-1 relative rounded-2xl overflow-hidden bg-background border border-border/50 shadow-sm group min-h-[180px] lg:min-h-[300px] hover:border-primary/20 transition-colors">
                        
                        {/* Processing Overlay */}
                        {isGenerating && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 z-20 backdrop-blur-sm">
                                <div className="relative mb-6">
                                    <div className="w-20 h-20 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                                    <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-primary animate-pulse" />
                                </div>
                                <p className="text-primary font-medium tracking-wide animate-pulse">Обработка фото...</p>
                            </div>
                        )}

                        {resultUrl ? (
                            // Result State
                            <div className="relative w-full h-full group">
                                <Image 
                                    src={resultUrl} 
                                    alt="Result" 
                                    fill 
                                    className="object-contain" 
                                    unoptimized
                                />
                                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-3">
                                    <Button variant="secondary" onClick={() => window.open(resultUrl, '_blank')}>
                                        Скачать
                                    </Button>
                                    <Button onClick={() => { /* logic to reset result if needed, but keeping simple */ }}>
                                        Еще раз
                                    </Button>
                                </div>
                            </div>
                        ) : inputImage ? (
                            // Input Image State
                            <div className="relative w-full h-full">
                                <Image 
                                    src={inputImage} 
                                    alt="Input" 
                                    fill 
                                    className="object-contain" 
                                />
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setIsMediaSelectorOpen(true)}
                                    className="absolute bottom-4 right-4 shadow-lg backdrop-blur-sm bg-background/80 hover:bg-background"
                                    disabled={isGenerating}
                                >
                                    <RefreshCw className="w-3 h-3 mr-2" />
                                    Заменить фото
                                </Button>
                            </div>
                        ) : (
                            // Empty / Upload State
                            <div
                                onClick={() => setIsMediaSelectorOpen(true)}
                                className="absolute inset-0 cursor-pointer hover:bg-muted/50 transition-all duration-300 flex flex-col items-center justify-center group p-3 lg:p-6"
                            >
                                <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full bg-background border-2 border-border flex items-center justify-center mb-2 lg:mb-6 group-hover:scale-110 group-hover:border-primary/50 transition-all duration-300 shadow-sm">
                                    <Camera className="w-6 h-6 lg:w-10 lg:h-10 text-muted-foreground group-hover:text-primary transition-colors" />
                                </div>
                                <h3 className="text-base lg:text-2xl font-bold text-foreground mb-1 lg:mb-3 group-hover:text-primary transition-colors">Загрузить фото</h3>
                                <p className="text-muted-foreground text-xs lg:text-sm max-w-xs text-center leading-relaxed px-3">
                                    Портрет или селфи
                                </p>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Panel 02 - Style Selection */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                    className="w-full shrink-0 lg:shrink lg:min-h-0 h-auto lg:h-full lg:w-[55%] xl:w-[55%] flex flex-col bg-background relative z-10"
                >
                    {/* Right Header */}
                    <div className={cn("px-4 lg:px-6 pt-4 lg:pt-6 pb-2 bg-background z-20 shrink-0 border-b border-transparent", isModal && "lg:pr-14")}>
                         <div className="flex items-center gap-2.5 shrink-0 h-10">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">2</div>
                            <h2 className="text-sm font-semibold text-foreground tracking-tight lg:font-bold lg:uppercase lg:tracking-wider">Стиль и параметры</h2>
                         </div>
                    </div>

                    {/* Scrollable Settings Content */}
                    <div className="flex-1 overflow-y-auto px-4 lg:px-6 pb-4 lg:pb-6 scrollbar-hide">
                        <div className="space-y-6 pt-2">

                            {/* Settings Section — placed above style picker so format/quality
                                stay in view while user scrolls through long preset grids. */}
                            <div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-muted-foreground">Формат</Label>
                                        <Select
                                            value={(modelParams.aspect_ratio as string) || "3:4"}
                                            onValueChange={(val) => setAspectRatio(val)}
                                        >
                                            <SelectTrigger className="h-9 text-xs bg-muted/30 border-border/50">
                                                <SelectValue placeholder="Формат" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1:1">Квадрат (1:1)</SelectItem>
                                                <SelectItem value="3:4">Портрет (3:4)</SelectItem>
                                                <SelectItem value="4:3">Альбом (4:3)</SelectItem>
                                                <SelectItem value="9:16">Сторис (9:16)</SelectItem>
                                                <SelectItem value="16:9">Кино (16:9)</SelectItem>
                                                <SelectItem value="2:3">Портрет (2:3)</SelectItem>
                                                <SelectItem value="3:2">Альбом (3:2)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-muted-foreground">Качество</Label>
                                        <Select
                                            value={(modelParams.quality as string) || "2K"}
                                            onValueChange={(val) => updateModelParam('quality', val)}
                                        >
                                            <SelectTrigger className="h-9 text-xs bg-muted/30 border-border/50">
                                                <SelectValue placeholder="Качество" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1K">Стандарт (1K)</SelectItem>
                                                <SelectItem value="2K">Высокое (2K)</SelectItem>
                                                <SelectItem value="4K">Ультра (4K)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-border" />

                            <Tabs defaultValue="sensualne" value={activeCategory} onValueChange={setActiveCategory} className="w-full">
                                {/* Desktop: shadcn segmented tabs (wraps cleanly).
                                    Mobile: pill scroll-x — mirrors miniapp
                                    PhotoshootModePanel and avoids the 7-tab squeeze
                                    that wrapped to two cramped lines. */}
                                <TabsList className="hidden md:flex w-full flex-wrap h-auto p-1 mb-4 justify-start bg-muted/50 sticky top-0 z-10">
                                    {CATEGORIES.map(cat => (
                                        <TabsTrigger key={cat.id} value={cat.id} className="flex-1 min-w-[80px]">
                                            {cat.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                                <div className="md:hidden flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide">
                                    {CATEGORIES.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setActiveCategory(cat.id)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap shrink-0",
                                                activeCategory === cat.id
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-popover text-muted-foreground border-border/40 hover:bg-muted",
                                            )}
                                        >
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-20">
                                    {filteredPresets.map(preset => (
                                        <button
                                            key={preset.id}
                                            onClick={() => setSelectedPresetId(preset.id)}
                                            className={cn(
                                                "group relative aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all text-left",
                                                selectedPresetId === preset.id
                                                    ? "border-primary ring-2 ring-primary/20"
                                                    : "border-transparent hover:border-primary/50"
                                            )}
                                        >
                                            <Image
                                                src={preset.thumbnail}
                                                alt={preset.nameEn}
                                                fill
                                                className="object-cover transition-transform group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                                            <span className="absolute bottom-2 left-2 right-2 text-white font-medium text-xs leading-tight line-clamp-2">
                                                {preset.nameEn}
                                            </span>
                                            {selectedPresetId === preset.id && (
                                                <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                                    <Check className="w-3 h-3" />
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </Tabs>
                        </div>
                    </div>

                </motion.div>

            </main>

            {/* Shared Footer - Generate Button */}
            <div className="border-t border-border bg-background/80 backdrop-blur-sm z-20 shrink-0 px-4 pt-2 pb-[calc(64px+env(safe-area-inset-bottom))] md:p-4 lg:p-6 lg:pb-6">
                <Button
                    onClick={handleGenerate}
                    disabled={!inputImage || isGenerating || price == null}
                    className={cn(
                        "w-full h-12 lg:h-14 text-sm lg:text-base font-bold tracking-tight lg:uppercase lg:tracking-widest shadow-xl transition-all duration-500",
                        (!inputImage)
                            ? "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                            : "bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-[1.02] hover:shadow-primary/25"
                    )}
                >
                    {isGenerating ? (
                        <GenerationTimer startedAt={generationStartedAt} label="Снимаем" />
                    ) : (
                        <span className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Создать фото {user && price != null ? `• ${price}` : ''}
                        </span>
                    )}
                </Button>
                {!isGenerating && !inputImage && (
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                        Загрузите фото-референс, чтобы продолжить
                    </p>
                )}
            </div>


            <MediaSelector
                open={isMediaSelectorOpen}
                onOpenChange={setIsMediaSelectorOpen}
                onSelect={(url) => {
                    setInputImage(url)
                    setIsMediaSelectorOpen(false)
                }}
            />
        </div>
    )
}
